/**
 * BATCH 2B — Canonical cycle phase configuration.
 *
 * Single source of truth for the 4-phase economic cycle taxonomy used
 * across the frontend (Dashboard, Portfolio Detail, Rebalancing, Evaluator,
 * Market Analysis, Advanced Cycle Analysis). All cycle phase rendering
 * MUST go through `getPhaseConfig()` and `normalizePhase()` here so that
 * styling, labels and ordering stay consistent with the backend canonical
 * endpoint at GET /api/v2/economic/cycle/current.
 *
 * Canonical taxonomy: expansion · peak · contraction · recovery
 *
 * Legacy variants ("boom", "recession", "trough") are normalized to the
 * canonical 4 keys so screens can call getPhaseConfig() with whatever the
 * upstream API returned.
 */
import { TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react';

export const CYCLE_PHASES_ORDER = ['expansion', 'peak', 'contraction', 'recovery'];

/**
 * Normalize any incoming phase string to one of the 4 canonical keys.
 * Returns lowercase canonical key. Returns 'unknown' when raw is null/empty
 * or an unrecognized value — callers must render a "no data" state.
 */
export const normalizePhase = (raw) => {
  const p = String(raw || '').trim().toLowerCase();
  if (!p) return 'unknown';
  // Match common multi-word variants like "Late Expansion", "Peak/Slowdown"
  if (p.includes('expansion')) return 'expansion';
  if (p.includes('recovery') || p.includes('trough')) return 'recovery';
  if (p.includes('contract') || p.startsWith('rec') || p.includes('bear')) return 'contraction';
  if (p.includes('peak') || p.includes('boom') || p.includes('slowdown')) return 'peak';
  if (p === 'bull') return 'expansion';
  return 'unknown';
};

/**
 * Master phase config consumed by every cycle-phase UI element.
 * Each entry exposes:
 *   - label        : Human-readable phase name
 *   - shortLabel   : Compact label for pills/badges
 *   - icon         : Lucide icon component
 *   - dot          : Hex color for the phase dot/legend
 *   - color        : Tailwind bg class for solid backgrounds
 *   - textColor    : Tailwind text class
 *   - bgColor      : Tailwind soft background class
 *   - borderColor  : Tailwind border class
 *   - badgeBg      : Tailwind badge bg
 *   - badgeText    : Tailwind badge text color
 *   - description  : Concise plain-English summary
 *   - bestSectors  : Sectors historically favored in this phase
 */
export const PHASE_CONFIG = {
  expansion: {
    label: 'Expansion',
    shortLabel: 'Expansion',
    icon: TrendingUp,
    dot: '#33cc99',
    color: 'bg-green-500',
    textColor: 'text-green-700 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/30',
    borderColor: 'border-green-500',
    badgeBg: 'bg-green-100 dark:bg-green-900/40',
    badgeText: 'text-green-800 dark:text-green-300',
    description:
      'Economic growth accelerates. GDP rises, unemployment falls, consumer confidence is high.',
    bestSectors: ['Technology', 'Consumer Discretionary', 'Industrials', 'Financials'],
  },
  peak: {
    label: 'Peak',
    shortLabel: 'Peak',
    icon: Minus,
    dot: '#BA7517',
    color: 'bg-yellow-500',
    textColor: 'text-yellow-700 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/30',
    borderColor: 'border-yellow-500',
    badgeBg: 'bg-yellow-100 dark:bg-yellow-900/40',
    badgeText: 'text-yellow-800 dark:text-yellow-300',
    description:
      'Growth reaches maximum levels. Inflation pressures build, central banks may raise rates.',
    bestSectors: ['Energy', 'Materials', 'Healthcare'],
  },
  contraction: {
    label: 'Contraction',
    shortLabel: 'Contraction',
    icon: TrendingDown,
    dot: '#E24B4A',
    color: 'bg-red-500',
    textColor: 'text-red-700 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/30',
    borderColor: 'border-red-500',
    badgeBg: 'bg-red-100 dark:bg-red-900/40',
    badgeText: 'text-red-800 dark:text-red-300',
    description:
      'Economic slowdown. GDP declines, unemployment rises. Defensive positioning is key.',
    bestSectors: ['Healthcare', 'Consumer Staples', 'Utilities'],
  },
  recovery: {
    label: 'Recovery',
    shortLabel: 'Recovery',
    icon: Activity,
    dot: '#378ADD',
    color: 'bg-blue-500',
    textColor: 'text-blue-700 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    borderColor: 'border-blue-500',
    badgeBg: 'bg-blue-100 dark:bg-blue-900/40',
    badgeText: 'text-blue-800 dark:text-blue-300',
    description:
      'Economy rebounds from lows. Early cyclicals shine, valuations attractive, low rates.',
    bestSectors: ['Financials', 'Industrials', 'Technology'],
  },
  unknown: {
    label: 'No Data',
    shortLabel: '—',
    icon: Minus,
    dot: '#9ca3af',
    color: 'bg-gray-400',
    textColor: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-800/40',
    borderColor: 'border-gray-400',
    badgeBg: 'bg-gray-100 dark:bg-gray-800/60',
    badgeText: 'text-gray-700 dark:text-gray-300',
    description:
      'Not enough economic cycle data available at the moment.',
    bestSectors: [],
  },
};

/**
 * Resolve any phase string to its config. Always returns a valid object —
 * never undefined. Unmapped / null values return the "unknown" config so the
 * UI can render a "No Data" state without breaking.
 */
export const getPhaseConfig = (phase) => PHASE_CONFIG[normalizePhase(phase)] || PHASE_CONFIG.unknown;

/**
 * Sectors favored per phase, used by Cycle Alignment scoring on
 * Portfolio Detail. Values are Title Case to match the canonical sector
 * vocabulary written by `app/api/v1/portfolio.py::resolve_instrument_sector`
 * (BATCH 3A). Comparison must be case-insensitive on the consumer side.
 *
 * BATCH 3C — These are the "aligned" tier of the 3-tier Cycle Alignment
 * model. The other two tiers are NEUTRAL_SECTORS (count 0.5×) and
 * everything else (count 0×). See `computeCycleAlignment` in
 * `pages/PortfolioDetail.js`.
 */
export const CYCLE_FAVORED_SECTORS = {
  expansion: [
    'Technology',
    'Financials',
    'Consumer Discretionary',
    'Industrials',
    'Communication Services',
  ],
  peak: ['Energy', 'Materials', 'Commodities', 'Real Estate'],
  contraction: [
    'Utilities',
    'Consumer Staples',
    'Healthcare',
    'Fixed Income',
  ],
  recovery: [
    'Technology',
    'Industrials',
    'Consumer Discretionary',
    'Financials',
    'Communication Services',
  ],
};


/**
 * BATCH 3C — Sectors that are appropriate in any cycle phase.
 * Holding these instruments neither helps nor hurts cycle alignment;
 * they count as 0.5× in the alignment score (vs 1.0× for favored
 * sectors and 0× for misaligned ones). This avoids penalizing
 * portfolios that include broad-market diversification, defensive
 * income strategies, or commodity / international exposure that simply
 * don't fit any single phase bucket.
 *
 * Comparison is case-insensitive against `instrument.sector` written
 * by `app/api/v1/portfolio.py::resolve_instrument_sector`.
 */
export const NEUTRAL_SECTORS = [
  'Diversified',
  'Equity Income',
  'Commodities',
  'International',
  'Emerging Markets',
  'Real Estate',
  'Gold',
  'Growth',
];
